/*
  Shared controller for the edd-selector snippet. Loaded as an external script so it works
  correctly even when the snippet is rendered more than once on the same page (e.g. two
  custom_edd blocks for an A/B test). Each rendered instance is fully independent: its own
  pincode, its own buffer_days and its own render. Only the widget carrying the session's
  control_group or variation_group class sends Mixpanel events. Checking the pincode
  on one widget never touches another widget's UI or fires an event for it. Only page-level
  side effects (the window.fetch wrap used for Add to Bag tracking, the variant:change
  listener, and the in-memory delivery-response cache) are shared/guarded on `window`, so the
  delivery API is never called twice for the same sku+pincode even though the widgets
  themselves don't talk to each other.
*/
(function () {
  var ENDPOINT = 'https://app.blissclub.com/api/customer/estimated-delivery';
  var PINCODE_STORAGE_KEY = 'kp_delivery_pincode';

  var state = window.__eddSelectorState || (window.__eddSelectorState = {
    activeSku: null,
    activeVariantId: null,
    productId: null,
    productName: null,
    productHandle: null,
    responseCache: {},
    pendingRequests: {},
    analyticsQueue: [],
    instances: []
  });

  function getResult(data) {
    return data && data.clickpost && data.clickpost.response && data.clickpost.response.result || {};
  }

  function getDeliveryBy(data, bufferDays) {
    var edd = getResult(data).edd;
    var maxDays = edd ? Number(edd.max) : NaN;
    var totalDays;
    var today;
    var date;
    var day;
    var suffix;

    if (Number.isNaN(maxDays) || maxDays < 0) return '';
    totalDays = maxDays + bufferDays;
    today = new Date();
    today.setHours(0, 0, 0, 0);
    date = new Date(today.getTime());
    date.setDate(date.getDate() + totalDays);
    // Keep the original EDD if a negative block adjustment lands on today or earlier.
    if (bufferDays < 0 && date <= today) {
      date = new Date(today.getTime());
      date.setDate(date.getDate() + maxDays);
    }
    day = date.getDate();
    suffix = day % 10 === 1 && day !== 11 ? 'st' : day % 10 === 2 && day !== 12 ? 'nd' : day % 10 === 3 && day !== 13 ? 'rd' : 'th';
    return day + suffix + ' ' + date.toLocaleString('en-IN', { month: 'short' });
  }

  function isCodAvailable(value) {
    if (typeof value === 'undefined') return null;
    return value === true || value === 1 || value === '1' || ['true', 'yes', 'y'].indexOf(String(value).toLowerCase()) > -1;
  }

  function getCod(data) {
    var result = getResult(data);
    var serviceability = data.courier_serviceable || data.courier_serviceability || result.courier_serviceable || result.courier_serviceability || {};
    var accounts = result.serviceable_accounts || [];
    var value = typeof serviceability.COD === 'undefined' ? serviceability.cod : serviceability.COD;
    var keys = ['is_cod_available', 'cash_on_delivery', 'cash_on_delivery_available', 'cod_available'];
    var sources = [data, result, serviceability];
    var sourceIndex;
    var keyIndex;
    var accountIndex;
    var hasCodResult = false;

    if (typeof value !== 'undefined') return isCodAvailable(value);

    for (sourceIndex = 0; sourceIndex < sources.length; sourceIndex += 1) {
      for (keyIndex = 0; keyIndex < keys.length; keyIndex += 1) {
        if (typeof sources[sourceIndex][keys[keyIndex]] !== 'undefined') {
          return isCodAvailable(sources[sourceIndex][keys[keyIndex]]);
        }
      }
    }

    // Clickpost returns COD per serviceable courier account.
    // COD is available when at least one courier accepts it.
    for (accountIndex = 0; accountIndex < accounts.length; accountIndex += 1) {
      if (accounts[accountIndex].serviceable && typeof accounts[accountIndex].serviceable.COD !== 'undefined') {
        hasCodResult = true;
        if (isCodAvailable(accounts[accountIndex].serviceable.COD)) return true;
      }
    }

    if (hasCodResult) return false;

    return null;
  }

  function isNotServiceable(data) {
    var clickpostMeta = data && data.clickpost && data.clickpost.response && data.clickpost.response.meta;
    var easyecomResponse = data && data.easyecom && data.easyecom.response;

    if (clickpostMeta && clickpostMeta.success === false) return true;
    if (easyecomResponse && easyecomResponse.code && easyecomResponse.code !== 200) return true;

    return false;
  }

  function setMessage(instance, text, isError) {
    instance.message.textContent = text || '';
    instance.message.classList.toggle('is-error', Boolean(isError));
  }

  function renderInstance(instance, data) {
    var deliveryBy = getDeliveryBy(data, instance.bufferDays);
    var codAvailable = getCod(data);

    if (!deliveryBy && codAvailable === null && isNotServiceable(data)) {
      instance.dateRow.hidden = true;
      instance.codRow.hidden = true;
      instance.status.hidden = true;
      instance.content.hidden = false;
      setMessage(instance, 'Pincode not serviceable', true);

      return { deliveryBy: '', codAvailable: null, bufferDays: instance.bufferDays };
    }

    instance.dateRow.hidden = !deliveryBy;
    instance.dateText.textContent = deliveryBy;
    instance.codRow.hidden = codAvailable === null;
    instance.codText.textContent = codAvailable === null ? '' : codAvailable ? 'Available' : 'Not available';
    instance.status.hidden = !deliveryBy && codAvailable === null;
    instance.content.hidden = false;
    setMessage(instance, '', false);

    return { deliveryBy: deliveryBy, codAvailable: codAvailable, bufferDays: instance.bufferDays };
  }

  function getMixpanelProperties(instance, details, response) {
    var responseData = null;
    var bufferKnown;
    var hasBuffer;
    var properties;

    details = details || {};
    bufferKnown = typeof details.bufferDays === 'number';
    hasBuffer = bufferKnown && details.bufferDays !== 0;

    try {
      responseData = response ? JSON.stringify(response) : null;
    } catch (error) {
      responseData = null;
    }

    properties = {
      pincode: instance.pincode || null,
      product_id: state.productId,
      product_name: state.productName,
      product_handle: state.productHandle,
      variant_id: state.activeVariantId,
      variant_sku: state.activeSku,
      delivery_by: details.deliveryBy || null,
      cod_available: typeof details.codAvailable === 'undefined' ? null : details.codAvailable,
      estimated_delivery_response: responseData,
      // A/B test: EDD shown with vs. without dispatch/handling buffer padding.
      // null when no delivery check has happened yet, so it never falsely tags an event as the control group.
      edd_variant: bufferKnown ? (hasBuffer ? 'with_buffer' : 'without_buffer') : null
    };

    if (hasBuffer) {
      properties.buffer_days = details.bufferDays;
    }

    return properties;
  }

  function getAssignedInstance() {
    var assigned = state.instances.filter(function (instance) {
      return instance.root.isConnected &&
        (instance.root.classList.contains('control_group') || instance.root.classList.contains('variation_group'));
    });
    return assigned.length === 1 ? assigned[0] : null;
  }

  function trackPincodeCheck(instance) {
    if (getAssignedInstance() !== instance || !instance.pendingCheckProperties) return;
    var properties = instance.pendingCheckProperties;
    instance.pendingCheckProperties = null;
    sendAnalytics('Pincode Checked', properties);
  }

  function flushAnalytics() {
    if (!window.mixpanel || typeof window.mixpanel.track !== 'function') return;
    while (state.analyticsQueue.length) {
      var event = state.analyticsQueue[0];
      window.mixpanel.track(event.name, event.properties);
      state.analyticsQueue.shift();
    }
    if (state.analyticsRetry) {
      clearTimeout(state.analyticsRetry);
      state.analyticsRetry = null;
    }
  }

  function sendAnalytics(name, properties) {
    state.analyticsQueue.push({ name: name, properties: properties });
    flushAnalytics();
    if (state.analyticsQueue.length && !state.analyticsRetry) {
      state.analyticsRetry = setTimeout(function retry() {
        state.analyticsRetry = null;
        flushAnalytics();
        if (state.analyticsQueue.length) state.analyticsRetry = setTimeout(retry, 500);
      }, 500);
    }
  }

  function trackAddToBag(properties, added) {
    if (!properties) return;
    var items = added.items || [added];
    var matches = items.some(function (item) {
      return String(item.variant_id || item.id) === String(properties.variant_id);
    });
    if (matches) sendAnalytics('Add to Bag', properties);
  }

  function forEachInstance(callback) {
    state.instances.forEach(callback);
  }

  function getOrFetchDeliveryData(requestKey, sku, pincode) {
    if (state.responseCache[requestKey]) {
      return Promise.resolve(state.responseCache[requestKey]);
    }

    if (state.pendingRequests[requestKey]) {
      return state.pendingRequests[requestKey];
    }

    var request = fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ sku: sku, pincode: pincode })
    }).then(function (response) {
      if (!response.ok) throw new Error('HTTP ' + response.status);
      return response.json();
    }).then(function (body) {
      var data = body.data || body.result || body;
      state.responseCache[requestKey] = data;
      return data;
    }).finally(function () {
      delete state.pendingRequests[requestKey];
    });

    state.pendingRequests[requestKey] = request;
    return request;
  }

  async function checkDelivery(instance) {
    var pincode = instance.pincode;
    if (!state.activeSku || pincode.length !== 6) return;

    var requestKey = state.activeSku + '|' + pincode;
    var variantId = state.activeVariantId;
    var requestId = ++instance.requestId;
    instance.pendingCheckProperties = null;
    instance.lastProperties = null;

    instance.checkButton.disabled = true;
    instance.checkButton.textContent = 'Checking';
    setMessage(instance, 'Checking delivery details...', false);

    try {
      var data = await getOrFetchDeliveryData(requestKey, state.activeSku, pincode);
      if (requestId !== instance.requestId || variantId !== state.activeVariantId) return;
      var details = renderInstance(instance, data);

      instance.lastDetails = details;
      instance.lastResponse = data;
      instance.lastProperties = getMixpanelProperties(instance, details, data);
      instance.pendingCheckProperties = instance.lastProperties;
      trackPincodeCheck(instance);
      return instance.lastProperties;
    } catch (error) {
      if (requestId !== instance.requestId) return;
      console.error('Estimated delivery error:', error);
      setMessage(instance, 'Unable to check delivery right now. Please try again.', true);
    } finally {
      if (requestId === instance.requestId) {
        instance.checkButton.disabled = false;
        instance.checkButton.textContent = 'Check';
      }
    }
  }

  function parseBufferDays(value) {
    var parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  function bindInstance(root) {
    var instance = {
      root: root,
      checker: root.querySelector('[data-delivery-checker]'),
      content: root.querySelector('[data-delivery-content]'),
      input: root.querySelector('[data-delivery-pincode]'),
      checkButton: root.querySelector('[data-delivery-check-button]'),
      message: root.querySelector('[data-delivery-message]'),
      status: root.querySelector('[data-delivery-cod-status]'),
      dateRow: root.querySelector('[data-delivery-date-row]'),
      dateText: root.querySelector('[data-delivery-date-text]'),
      codRow: root.querySelector('[data-delivery-cod-row]'),
      codText: root.querySelector('[data-delivery-cod-text]'),
      bufferDays: parseBufferDays(root.getAttribute('data-buffer-days')),
      requestId: 0,
      lastProperties: null,
      pendingCheckProperties: null,
      pincode: localStorage.getItem(PINCODE_STORAGE_KEY) || '',
      lastDetails: null,
      lastResponse: null
    };

    instance.input.value = instance.pincode;

    instance.checker.addEventListener('submit', function (event) {
      event.preventDefault();
      var pincode = instance.input.value.replace(/\D/g, '').slice(0, 6);
      instance.input.value = pincode;

      if (pincode.length !== 6) {
        setMessage(instance, 'Please enter a valid 6 digit pincode.', true);
        return;
      }

      instance.pincode = pincode;
      localStorage.setItem(PINCODE_STORAGE_KEY, pincode);
      instance.checkPromise = checkDelivery(instance);
    });

    state.instances.push(instance);

    // The A/B tool may assign the session after an automatic delivery check finishes.
    var observer = new MutationObserver(function () {
      forEachInstance(trackPincodeCheck);
    });
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });

    if (instance.pincode.length === 6 && state.activeSku) {
      instance.checkPromise = checkDelivery(instance);
    }
  }

  function init() {
    var newRoots = document.querySelectorAll('[data-delivery-selector]:not([data-delivery-bound])');
    if (!newRoots.length) return;

    newRoots.forEach(function (root) {
      root.setAttribute('data-delivery-bound', 'true');

      if (!state.productId) {
        state.productId = root.getAttribute('data-product-id');
        state.productName = root.getAttribute('data-product-name');
        state.productHandle = root.getAttribute('data-product-handle');
        state.activeSku = root.getAttribute('data-sku');
        state.activeVariantId = root.getAttribute('data-variant-id');
      }

      bindInstance(root);
    });

    if (!state.analyticsListenerAdded) {
      state.analyticsListenerAdded = true;
      document.addEventListener('bliss:mixpanel:activate', flushAnalytics);
    }

    if (!window.__eddSelectorFetchWrapped) {
      window.__eddSelectorFetchWrapped = true;

      // Send Add to Bag only after Shopify confirms the add request succeeded.
      var originalFetch = window.fetch;
      window.fetch = function (resource) {
        var url = typeof resource === 'string' ? resource : resource && resource.url ? resource.url : '';
        var assigned = getAssignedInstance();
        // Capture the checked result before the cart request, so later UI changes cannot
        // attribute this conversion to another pincode or product variant.
        var properties = assigned && assigned.lastProperties;
        var pendingCheck = assigned && assigned.checkPromise;
        var variantId = state.activeVariantId;
        var pincode = assigned && assigned.pincode;
        var request = originalFetch.apply(this, arguments);

        if (url.indexOf('/cart/add') !== -1) {
          request.then(function (response) {
            if (response.ok && assigned) {
              return Promise.all([response.clone().json(), properties || pendingCheck]).then(function (results) {
                var checked = results[1];
                if (checked && String(checked.variant_id) === String(variantId) && checked.pincode === pincode) {
                  trackAddToBag(checked, results[0]);
                }
              });
            }
          }).catch(function () {});
        }

        return request;
      };
    }

    if (!window.__eddSelectorVariantListenerAdded) {
      window.__eddSelectorVariantListenerAdded = true;

      document.addEventListener('variant:change', function (event) {
        var variant = event.detail && event.detail.variant;
        if (!variant) return;
        if (event.detail.product && String(event.detail.product.id) !== String(state.productId)) return;
        if (String(variant.id) === String(state.activeVariantId) && (variant.sku || null) === state.activeSku) return;

        state.activeSku = variant.sku || null;
        state.activeVariantId = variant.id || state.activeVariantId;

        // The product changed under every widget, so each one refreshes itself using
        // its own pincode; getOrFetchDeliveryData still collapses this to one network
        // call when two widgets share the same pincode.
        forEachInstance(function (instance) {
          instance.requestId += 1;
          instance.pendingCheckProperties = null;
          instance.lastProperties = null;
          instance.checkButton.disabled = false;
          instance.checkButton.textContent = 'Check';
          instance.checkPromise = instance.pincode.length === 6 ? checkDelivery(instance) : null;
        });
      });
    }
  }

  init();
})();
